import { useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import InputPriceRate from 'components/others/InputPriceRate';
import { useSettings } from 'context/SettingsContext';
import {
    getBaseCurrencyCode,
    getCurrencyCode,
    getCurrencyI18nParams,
    getIsLocalCurrencyMode,
    getLocalCurrencyPrefix,
} from 'utils/helpers/currency/currencyHelpers';
import { formatPrice } from 'utils/helpers/functions/pricesFunctions';

export interface TaskPayValue {
    pay: boolean;
    payAmount: number | null;
    /** Сума зафіксована в базовій валюті. Інакше — у місцевій, уже за курсом на момент збереження. */
    payUsd: boolean;
    showPayToExecutor: boolean;
}

interface TaskPayFieldsProps {
    idPrefix: string;
    value: TaskPayValue;
    disabled?: boolean;
    readOnly?: boolean;
    onChange: (next: TaskPayValue) => void;
}

const roundMoney = (value: number) => Number(value.toFixed(2));

/** Як getUseUsd у шаблонах: при курсі 1 або одній валюті прапорець базової валюти не діє. */
export const isTaskPayUsd = (payUsd: boolean, rate: number) =>
    !getIsLocalCurrencyMode(rate) && Boolean(payUsd);

export const formatTaskPayAmount = (amount: number, payUsd: boolean, rate: number) =>
    formatPrice(amount, isTaskPayUsd(payUsd, rate) ? getBaseCurrencyCode() : getCurrencyCode());

const TaskPayFields = ({ idPrefix, value, disabled = false, readOnly = false, onChange }: TaskPayFieldsProps) => {
    const { t } = useTranslation(['Tasks']);
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const localMode = getIsLocalCurrencyMode(rate);
    const useUsd = isTaskPayUsd(value.payUsd, rate);
    const switchId = `${idPrefix}-pay`;
    const usdId = `${idPrefix}-pay-usd`;
    const showId = `${idPrefix}-pay-show`;
    const priceRef = useRef<HTMLInputElement>(null);
    const payWasOn = useRef(value.pay);

    useEffect(() => {
        if (value.pay && !payWasOn.current) {
            requestAnimationFrame(() => {
                const input = priceRef.current;
                if (!input) return;
                input.focus();
                input.select();
            });
        }
        payWasOn.current = value.pay;
    }, [value.pay]);

    if (readOnly) {
        if (!value.pay) return null;
        return (
            <div className="mt-2">
                <div className="text-muted fs-12">{t('labelPayment')}</div>
                <div className="text-body">{formatTaskPayAmount(Number(value.payAmount || 0), value.payUsd, rate)}</div>
            </div>
        );
    }

    const setAmount = (amount: number | null) => {
        onChange({
            ...value,
            pay: true,
            payUsd: useUsd,
            payAmount: amount,
        });
    };

    return (
        <div className="mb-3">
            <div className="form-check form-switch mb-2">
                <input
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    id={switchId}
                    checked={value.pay}
                    disabled={disabled}
                    onChange={(e) => {
                        const pay = e.target.checked;
                        onChange(
                            pay
                                ? { ...value, pay: true, payUsd: localMode ? false : value.payUsd }
                                : { pay: false, payAmount: null, payUsd: !localMode, showPayToExecutor: false },
                        );
                    }}
                />
                <label className="form-check-label" htmlFor={switchId}>
                    <span className="d-block">{t('labelPayment')}</span>
                    <span className="d-block text-muted fs-12">{t('hintPayment')}</span>
                </label>
            </div>
            {value.pay && (
                <div className="ps-4">
                    <Form.Group className="mb-2">
                        {useUsd ? (
                            <InputPriceRate
                                ref={priceRef}
                                value={Number(value.payAmount || 0)}
                                disabled={disabled}
                                allowNegative={false}
                                onValueChange={(val) => {
                                    setAmount(val?.floatValue == null ? null : Number(val.floatValue));
                                }}
                            />
                        ) : (
                            <NumericFormat
                                getInputRef={priceRef}
                                className="form-control"
                                disabled={disabled}
                                allowNegative={false}
                                prefix={getLocalCurrencyPrefix()}
                                thousandSeparator=" "
                                decimalSeparator="."
                                allowedDecimalSeparators={['.', ',']}
                                decimalScale={2}
                                value={value.payAmount ?? ''}
                                onValueChange={(val) => {
                                    setAmount(val.floatValue == null ? null : Number(val.floatValue));
                                }}
                                onFocus={(e) => e.target.select()}
                            />
                        )}
                    </Form.Group>
                    {!localMode && (
                        <div className="form-check mb-2">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={usdId}
                                checked={Boolean(value.payUsd)}
                                disabled={disabled}
                                onChange={(e) => {
                                    const payUsd = e.target.checked;
                                    const current = Number(value.payAmount || 0);
                                    const safeRate = rate > 0 ? rate : 1;
                                    const payAmount = payUsd
                                        ? roundMoney(current / safeRate)
                                        : roundMoney(current * safeRate);
                                    onChange({ ...value, pay: true, payUsd, payAmount });
                                }}
                            />
                            <label className="form-check-label" htmlFor={usdId}>
                                {t('labelPayInBase', getCurrencyI18nParams())}
                            </label>
                        </div>
                    )}
                    <div className="form-check mb-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={showId}
                            checked={value.showPayToExecutor}
                            disabled={disabled}
                            onChange={(e) => onChange({ ...value, showPayToExecutor: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor={showId}>
                            {t('labelShowPayToExecutor')}
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskPayFields;
